import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import type { RootState } from "../rootReducer";

export type FieldTeamRole = "field_technician" | "admin";

export interface FieldTeamMember {
  id: string;
  name: string;
  email: string;
  role: FieldTeamRole;
  /** Cognito custom attributes; may be empty for legacy persisted sessions. */
  firstName?: string;
  lastName?: string;
  /** Cognito groups, when known, for gating mode-switch affordances. */
  groups?: string[];
}

/**
 * Top-level UI mode for users that have access to multiple app shells.
 * Configurator app currently only ships the setup mode, but the type +
 * surrounding plumbing match PatientApp-Web so the mode-switch hook can
 * be reused without code drift.
 */
export type AppMode = "patient" | "setup";

/** localStorage key for persisting the user's mode choice across reloads. */
export const APP_MODE_STORAGE_KEY = "circadia_app_mode";

const readPersistedAppMode = (): AppMode | undefined => {
  try {
    if (typeof window === "undefined") return undefined;
    const v = window.localStorage.getItem(APP_MODE_STORAGE_KEY);
    return v === "patient" || v === "setup" ? v : undefined;
  } catch {
    return undefined;
  }
};

const writePersistedAppMode = (mode: AppMode | undefined): void => {
  try {
    if (typeof window === "undefined") return;
    if (mode === undefined) {
      window.localStorage.removeItem(APP_MODE_STORAGE_KEY);
    } else {
      window.localStorage.setItem(APP_MODE_STORAGE_KEY, mode);
    }
  } catch {
    /* best-effort */
  }
};

export interface AuthState {
  isAuthenticated: boolean;
  fieldTeamMember: FieldTeamMember | null;
  token: string | null;
  /** Which app shell the user is currently in. Persisted to localStorage. */
  appMode?: AppMode;
  /**
   * Set while transitioning between app shells (setup ⇄ patient). Drives the
   * full-screen overlay so the user doesn't see the old shell tear down and
   * the new one paint underneath. Not persisted — purely UI.
   */
  modeSwitching?: { target: AppMode };
}

const name = "Auth";

export const initialState: AuthState = {
  isAuthenticated: false,
  fieldTeamMember: null,
  token: null,
  appMode: readPersistedAppMode(),
  modeSwitching: undefined,
};

export const authSlice = createSlice({
  name,
  initialState,
  reducers: {
    setAuth: (
      state,
      action: PayloadAction<{
        fieldTeamMember: FieldTeamMember;
        token: string;
      }>
    ) => {
      state.isAuthenticated = true;
      state.fieldTeamMember = action.payload.fieldTeamMember;
      state.token = action.payload.token;
    },
    clearAuth: () => ({ ...initialState, appMode: undefined }),
    /** Latest Id token from Amplify or native `cognitoTokens()` (after refresh). */
    updateIdToken: (state, action: PayloadAction<string>) => {
      if (state.isAuthenticated && action.payload) {
        state.token = action.payload;
      }
    },
    setAppMode: (state, action: PayloadAction<AppMode | undefined>) => {
      state.appMode = action.payload;
      writePersistedAppMode(action.payload);
    },
    setModeSwitching: (
      state,
      action: PayloadAction<{ target: AppMode } | undefined>
    ) => {
      state.modeSwitching = action.payload;
    },
  },
});

export const {
  setAuth,
  clearAuth,
  updateIdToken,
  setAppMode,
  setModeSwitching,
} = authSlice.actions;

export const selectIsAuthenticated = (state: RootState): boolean =>
  state.auth.isAuthenticated;

export const selectFieldTeamMember = (state: RootState) =>
  state.auth.fieldTeamMember;

export const selectToken = (state: RootState): string | null =>
  state.auth.token;

const persistedAuthReducer = persistReducer(
  {
    key: "auth",
    storage,
    whitelist: ["isAuthenticated", "fieldTeamMember", "token"],
  },
  authSlice.reducer
);

export default persistedAuthReducer;
